// src/screens/BalanceSheet.tsx
// ─────────────────────────────────────────────────────────────────────────────
// M3 — Balance Sheet. Five wealth pots with current balances and projections.
// Next button hidden for 5 seconds to let the number land.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import { POT_COLOURS } from '../constants/potColours';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

// ── Wealth percentile lookup (ONS Wealth and Assets Survey) ─────────────────
function getWealthPercentile(netWorth: number, age: number): number {
  if (age < 35) {
    if (netWorth >= 500000) return 95;
    if (netWorth >= 250000) return 90;
    if (netWorth >= 150000) return 80;
    if (netWorth >= 75000)  return 70;
    if (netWorth >= 30000)  return 50;
    if (netWorth >= 10000)  return 30;
    return 10;
  } else if (age < 45) {
    if (netWorth >= 900000) return 95;
    if (netWorth >= 500000) return 90;
    if (netWorth >= 300000) return 80;
    if (netWorth >= 150000) return 70;
    if (netWorth >= 60000)  return 50;
    if (netWorth >= 20000)  return 30;
    return 10;
  } else if (age < 55) {
    if (netWorth >= 1200000) return 95;
    if (netWorth >= 700000)  return 90;
    if (netWorth >= 400000)  return 80;
    if (netWorth >= 200000)  return 70;
    if (netWorth >= 80000)   return 50;
    if (netWorth >= 25000)   return 30;
    return 10;
  } else if (age < 65) {
    if (netWorth >= 1500000) return 95;
    if (netWorth >= 900000)  return 90;
    if (netWorth >= 550000)  return 80;
    if (netWorth >= 300000)  return 70;
    if (netWorth >= 120000)  return 50;
    if (netWorth >= 40000)   return 30;
    return 10;
  } else {
    if (netWorth >= 1800000) return 95;
    if (netWorth >= 1000000) return 90;
    if (netWorth >= 600000)  return 80;
    if (netWorth >= 350000)  return 70;
    if (netWorth >= 150000)  return 50;
    if (netWorth >= 50000)   return 30;
    return 10;
  }
}

// ── Pension wealth percentile (ONS WAS — private pension wealth by age band) ─
function getPensionWealthPercentile(pension: number, age: number): number {
  if (age < 35) {
    if (pension >= 100000) return 90; if (pension >= 50000) return 80;
    if (pension >= 20000) return 70; if (pension >= 8000) return 60;
    if (pension >= 2000) return 50; return 30;
  } else if (age < 45) {
    if (pension >= 250000) return 90; if (pension >= 120000) return 80;
    if (pension >= 60000) return 70; if (pension >= 25000) return 60;
    if (pension >= 8000) return 50; return 30;
  } else if (age < 55) {
    if (pension >= 450000) return 90; if (pension >= 220000) return 80;
    if (pension >= 110000) return 70; if (pension >= 50000) return 60;
    if (pension >= 15000) return 50; return 30;
  } else if (age < 65) {
    if (pension >= 600000) return 90; if (pension >= 300000) return 80;
    if (pension >= 150000) return 70; if (pension >= 70000) return 60;
    if (pension >= 20000) return 50; return 30;
  } else {
    if (pension >= 500000) return 90; if (pension >= 250000) return 80;
    if (pension >= 120000) return 70; if (pension >= 50000) return 60;
    if (pension >= 15000) return 50; return 30;
  }
}

// ── Property wealth percentile (ONS WAS — property equity by age band) ───────
function getPropertyWealthPercentile(equity: number, age: number): number {
  if (equity <= 0) return 35; // no property equity — bottom third approximately
  if (age < 35) {
    if (equity >= 300000) return 95; if (equity >= 150000) return 85;
    if (equity >= 80000) return 75; if (equity >= 30000) return 65;
    return 40;
  } else if (age < 45) {
    if (equity >= 400000) return 95; if (equity >= 220000) return 85;
    if (equity >= 120000) return 75; if (equity >= 50000) return 65;
    return 45;
  } else if (age < 55) {
    if (equity >= 500000) return 95; if (equity >= 300000) return 85;
    if (equity >= 180000) return 75; if (equity >= 80000) return 65;
    return 50;
  } else if (age < 65) {
    if (equity >= 600000) return 95; if (equity >= 380000) return 85;
    if (equity >= 230000) return 75; if (equity >= 100000) return 65;
    return 55;
  } else {
    if (equity >= 700000) return 95; if (equity >= 400000) return 85;
    if (equity >= 250000) return 75; if (equity >= 120000) return 65;
    return 55;
  }
}

// ── Financial wealth percentile (ONS WAS — savings + investments excl. pension/property) ─
function getFinancialWealthPercentile(financial: number, age: number): number {
  if (age < 35) {
    if (financial >= 80000) return 90; if (financial >= 30000) return 80;
    if (financial >= 12000) return 70; if (financial >= 4000) return 60;
    if (financial >= 1000) return 50; return 30;
  } else if (age < 45) {
    if (financial >= 120000) return 90; if (financial >= 50000) return 80;
    if (financial >= 20000) return 70; if (financial >= 7000) return 60;
    if (financial >= 2000) return 50; return 30;
  } else if (age < 55) {
    if (financial >= 180000) return 90; if (financial >= 80000) return 80;
    if (financial >= 35000) return 70; if (financial >= 12000) return 60;
    if (financial >= 3000) return 50; return 30;
  } else if (age < 65) {
    if (financial >= 250000) return 90; if (financial >= 110000) return 80;
    if (financial >= 50000) return 70; if (financial >= 18000) return 60;
    if (financial >= 5000) return 50; return 30;
  } else {
    if (financial >= 300000) return 90; if (financial >= 130000) return 80;
    if (financial >= 60000) return 70; if (financial >= 22000) return 60;
    if (financial >= 6000) return 50; return 30;
  }
}

// Simple compound growth: balance × (1+r)^n + monthly × ((1+r)^n - 1) / r  (monthly r)
function projectBalance(balance: number, monthlyContrib: number, annualRate: number, years: number): number {
  if (years <= 0) return balance;
  const monthlyRate = annualRate / 12;
  const months = years * 12;
  if (monthlyRate === 0) return balance + monthlyContrib * months;
  const growth = Math.pow(1 + monthlyRate, months);
  return balance * growth + monthlyContrib * ((growth - 1) / monthlyRate);
}

export default function BalanceSheet({ data }: Props) {
  const [showReady, setShowReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowReady(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const yearsToFreedom = Math.max(0, data.targetFreedomAge - data.clientAge);

  // ── Cash ──────────────────────────────────────────────────────────────────
  const savingsAccounts = data.savingsAccounts || [];
  const savingsTotal = savingsAccounts.reduce((sum, a) => sum + (a.balance || 0), 0);
  const efTotal = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0);
  const totalCash = savingsTotal + efTotal;
  const cashMonthlyTopUp = data.efTopUp || 0;
  const cashProjected = projectBalance(totalCash, cashMonthlyTopUp, data.assumptionCashRate ?? 0.01, yearsToFreedom);

  // ── Pension ───────────────────────────────────────────────────────────────
  const pensionMonthly = (data.pensionMonthly || 0) + (data.pensionCompanyFunded || 0);
  const pensionProjected = projectBalance(data.pensionBalance || 0, pensionMonthly, data.assumptionPensionGrowth ?? 0.08, yearsToFreedom);

  // ── ISA ───────────────────────────────────────────────────────────────────
  const isaProjected = projectBalance(data.investmentsBalance || 0, data.isaContribution || 0, data.assumptionIsaGrowth ?? 0.08, yearsToFreedom);

  // ── Property ──────────────────────────────────────────────────────────────
  const residentialEquity = (data.propertyValue || 0) - (data.mortgageBalance || 0);
  const rentalEquity = (data.rentalPropertyValue || 0) - (data.rentalMortgageBalance || 0);
  const totalPropertyEquity = residentialEquity + rentalEquity;

  // ── Business ──────────────────────────────────────────────────────────────
  const dlaBalance = data.dlaBalance || 0;

  // ── Summary ───────────────────────────────────────────────────────────────
  const totalAssets = totalCash + (data.pensionBalance || 0) + (data.isaBalance || 0) + (data.propertyValue || 0) + (data.rentalPropertyValue || 0) + dlaBalance;
  const totalDebt = (data.mortgageBalance || 0) + (data.rentalMortgageBalance || 0) + (data.consumerDebtTotal || 0);
  const netWorth = totalAssets - totalDebt;
  const liquid = totalCash + (data.isaBalance || 0) + (data.pensionBalance || 0);
  const illiquid = totalPropertyEquity;

  // ── Projected net worth at freedom age ──────────────────────────────────
  // Property value at freedom age
  const propertyValueAtFreedom = (data.propertyValue || 0) * Math.pow(1 + (data.assumptionPropertyGrowth ?? 0.01), yearsToFreedom);

  // Residential mortgage remaining balance at freedom age (standard amortisation)
  const annualMortgageRate = (data.mortgageRate || 0) / 100; // stored as percentage
  const monthlyMortgageRate = annualMortgageRate / 12;
  const totalMortgageMonths = (data.mortgageTermYears || 25) * 12;
  const paymentsMade = Math.min(yearsToFreedom * 12, totalMortgageMonths);
  const remainingPayments = totalMortgageMonths - paymentsMade;
  const mortgageBalanceAtFreedom = monthlyMortgageRate > 0 && remainingPayments > 0
    ? (data.mortgageBalance || 0) * Math.pow(1 + monthlyMortgageRate, paymentsMade)
      - (data.mortgagePayment || 0) * (Math.pow(1 + monthlyMortgageRate, paymentsMade) - 1) / monthlyMortgageRate
    : 0;

  const residentialEquityAtFreedom = Math.max(0, propertyValueAtFreedom - Math.max(0, mortgageBalanceAtFreedom));

  // Rental property — grow equity by property growth rate
  const rentalEquityAtFreedom = ((data.rentalPropertyValue || 0) - (data.rentalMortgageBalance || 0)) * Math.pow(1 + (data.assumptionPropertyGrowth ?? 0.01), yearsToFreedom);

  // Total projected values at freedom age
  const projectedTotalAssets = cashProjected + pensionProjected + isaProjected + propertyValueAtFreedom
    + ((data.rentalPropertyValue || 0) * Math.pow(1 + (data.assumptionPropertyGrowth ?? 0.01), yearsToFreedom));
  const projectedTotalDebt = Math.max(0, mortgageBalanceAtFreedom);
  const projectedNetWorthAtFreedom = cashProjected + pensionProjected + isaProjected
    + residentialEquityAtFreedom + Math.max(0, rentalEquityAtFreedom);

  // ── Wealth benchmarking — ONS four-category breakdown ─────────────────────
  const clientAge = data.clientAge || 0;
  const pensionTotal = data.pensionBalance || data.pensionBalanceDC || 0;
  const pensionPct = getPensionWealthPercentile(pensionTotal, clientAge);
  const propertyPct = getPropertyWealthPercentile(totalPropertyEquity, clientAge);
  const financialTotal = (data.isaBalance || data.isaStocksShares || 0) + efTotal + savingsTotal;
  const financialPct = getFinancialWealthPercentile(financialTotal, clientAge);
  const netWorthPct = getWealthPercentile(netWorth, clientAge);

  function benchBadge(percentile: number): { label: string; cls: string } {
    const isAbove = percentile >= 50;
    const rank = isAbove ? 100 - percentile : percentile;
    const label = isAbove ? `Top ${rank}%` : `Bottom ${rank}%`;
    if (percentile >= 80) return { label, cls: 'bg-green-100 text-green-700' };
    if (percentile >= 50) return { label, cls: 'bg-blue-50 text-blue-700' };
    return { label, cls: 'bg-amber-50 text-amber-700' };
  }

  // ── Year-by-year wealth trajectory chart data ─────────────────────────────
  const freedomAge = data.targetFreedomAge || 60;
  const maxYears = Math.max(0, freedomAge - (data.clientAge || 0));

  // Total annual contributions (for annual view chart)
  const totalAnnualContribs = useMemo(() => {
    const totalPensionMonthly = (data.pensionMonthly || 0) + (data.pensionCompanyFunded || 0)
    const isaMonthly = data.isaContribution || 0
    const savingsMonthly = data.efTopUp || 0
    // Mortgage capital repayment — use same calculation as in the main chart useMemo
    const annualRate = (data.mortgageRate || 0) / 100
    const monthlyRate = annualRate / 12
    const monthlyInterest = (data.mortgageBalance || 0) * monthlyRate
    const monthlyCapital = Math.max(0, (data.mortgagePayment || 0) - monthlyInterest)
    const result = ((totalPensionMonthly + isaMonthly + savingsMonthly) * 12) + (monthlyCapital * 12)
    console.log('[BalanceSheet] totalAnnualContribs =', result)
    return result
  }, [data.pensionMonthly, data.pensionCompanyFunded, data.isaContribution, data.efTopUp,
      data.mortgageRate, data.mortgageBalance, data.mortgagePayment])

  const chartData = useMemo(() => {
    const pensionRate = data.assumptionPensionGrowth ?? 0.08;
    const isaRate = data.assumptionIsaGrowth ?? 0.08;
    const cashRate = data.assumptionCashRate ?? 0.01;
    const propRate = data.assumptionPropertyGrowth ?? 0.01;
    // Include ALL cash: EF accounts + savings accounts (Brief 46B S4 fix)
    const savingsAccountsTotal = (data.savingsAccounts || []).reduce((sum: number, a: { balance?: number }) => sum + (a.balance || 0), 0);
    const totalCashNow = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0) + savingsAccountsTotal;
    const pensionContribAnnual = ((data.pensionMonthly || 0) + (data.pensionCompanyFunded || 0)) * 12;
    const isaContribAnnual = (data.isaContribution || 0) * 12;
    const cashContribAnnual = (data.efTopUp || 0) * 12;

    const totalPensionMonthly = (data.pensionMonthly || 0) + (data.pensionCompanyFunded || 0);
    const isaMonthly = data.isaContribution || 0;
    const savingsMonthly = data.efTopUp || 0;

    function fvLump(pv: number, rate: number, years: number): number {
      return pv * Math.pow(1 + rate, years);
    }
    function fvContribs(monthlyPmt: number, rate: number, years: number): number {
      if (rate === 0) return monthlyPmt * 12 * years;
      return monthlyPmt * 12 * (Math.pow(1 + rate, years) - 1) / rate;
    }

    const mortgageAnnualRate = (data.mortgageRate || 0) / 100; // stored as percentage
    const mortgageMonthlyRate = mortgageAnnualRate / 12;
    const totalPayments = (data.mortgageTermYears || 25) * 12;

    // Contributions vs Growth tracking
    const initialBalances = (data.pensionBalance || data.pensionBalanceDC || 0)
      + (data.investmentsBalance || 0)
      + totalCashNow;
    const annualContribs = (totalPensionMonthly * 12) + (isaMonthly * 12) + (savingsMonthly * 12);

    return Array.from({ length: maxYears + 1 }, (_, t) => {
      const pension_t = fvLump(data.pensionBalance || 0, pensionRate, t)
        + fvContribs(totalPensionMonthly, pensionRate, t);
      const isa_t = fvLump(data.investmentsBalance || 0, isaRate, t)
        + fvContribs(isaMonthly, isaRate, t);
      const cash_t = fvLump(totalCashNow, cashRate, t)
        + fvContribs(savingsMonthly, cashRate, t);
      const propValue_t = fvLump(data.propertyValue || 0, propRate, t);

      const paymentsMadeT = Math.min(t * 12, totalPayments);
      const remainingPaymentsT = totalPayments - paymentsMadeT;
      const mortgageBalance_t = mortgageMonthlyRate > 0 && remainingPaymentsT > 0
        ? Math.max(0, (data.mortgageBalance || 0) * Math.pow(1 + mortgageMonthlyRate, paymentsMadeT)
          - (data.mortgagePayment || 0) * (Math.pow(1 + mortgageMonthlyRate, paymentsMadeT) - 1) / mortgageMonthlyRate)
        : 0;
      const propertyEquity_t = Math.max(0, propValue_t - mortgageBalance_t);

      // Rental property equity — grow by property growth rate (Brief 46B S4 fix)
      const rentalEquity_t = ((data.rentalPropertyValue || 0) - (data.rentalMortgageBalance || 0))
        * Math.pow(1 + propRate, t);

      // Contributions vs Growth
      const cumulativeContribs = initialBalances + (annualContribs * t);
      const totalWealth = pension_t + isa_t + cash_t + propertyEquity_t + Math.max(0, rentalEquity_t);
      const cumulativeGrowth = Math.max(0, totalWealth - cumulativeContribs);

      const liquidTotal_t = pension_t + isa_t + cash_t;
      const liquidGrowth_t = Math.max(0, liquidTotal_t - cumulativeContribs);

      return {
        age: (data.clientAge || 0) + t,
        pension: Math.round(pension_t),
        isa: Math.round(isa_t),
        cash: Math.round(cash_t),
        propertyEquity: Math.round(propertyEquity_t),
        rentalEquity: Math.round(Math.max(0, rentalEquity_t)),
        mortgage: Math.round(mortgageBalance_t),
        contributions: Math.round(cumulativeContribs),
        growth: Math.round(cumulativeGrowth),
        totalWealth: Math.round(totalWealth),
        liquidTotal: Math.round(liquidTotal_t),
        liquidGrowth: Math.round(liquidGrowth_t),
        // Pass rates through for second useMemo
        pensionRate,
        isaRate,
        propRate,
        cashRate,
        totalCashNow,
        pensionContribAnnual,
        isaContribAnnual,
        cashContribAnnual,
      };
    });
  }, [data.clientAge, maxYears, data.pensionBalance, data.investmentsBalance, data.propertyValue,
      data.mortgageBalance, data.mortgagePayment, data.mortgageRate, data.mortgageTermYears,
      data.pensionMonthly, data.pensionCompanyFunded, data.isaContribution, data.efTopUp,
      data.efAccount1Balance, data.efAccount2Balance, data.savingsAccounts,
      data.rentalPropertyValue, data.rentalMortgageBalance,
      data.assumptionPensionGrowth, data.assumptionIsaGrowth, data.assumptionPropertyGrowth, data.assumptionCashRate]);

  // ── Contributions vs Growth — second pass for annual view + crossovers ──
  const chartDataWithGrowth = useMemo(() => {
    // Opening balances for i===0 growth calc
    const openingPension = data.pensionBalance || data.pensionBalanceDC || 0;
    const openingIsa = data.investmentsBalance || 0;
    const openingProperty = data.propertyValue || 0;

    return chartData.map((d, i) => {
      const annualContribution = Math.round(totalAnnualContribs);

      // Per-pot annual growth — pure compound return, NOT equity change
      const pensionGrowthVal = i === 0
        ? Math.round(openingPension * d.pensionRate)
        : Math.max(0, (d.pension - chartData[i - 1].pension) - d.pensionContribAnnual);

      const isaGrowthVal = i === 0
        ? Math.round(openingIsa * d.isaRate)
        : Math.max(0, (d.isa - chartData[i - 1].isa) - d.isaContribAnnual);

      const cashGrowthVal = i === 0
        ? Math.round(d.totalCashNow * d.cashRate)
        : Math.max(0, (d.cash - chartData[i - 1].cash) - d.cashContribAnnual);

      // Property growth = pure price appreciation (NOT equity delta which includes capital repayment)
      const propGrowthVal = i === 0
        ? Math.round(openingProperty * d.propRate)
        : Math.round(openingProperty * Math.pow(1 + d.propRate, i) - openingProperty * Math.pow(1 + d.propRate, i - 1));

      const annualGrowthValue = pensionGrowthVal + isaGrowthVal + cashGrowthVal + propGrowthVal;

      return {
        ...d,
        annualContribution,
        annualGrowth: Math.round(annualGrowthValue),
        // Per-pot for tooltip
        pensionGrowth: Math.round(pensionGrowthVal),
        isaGrowth: Math.round(isaGrowthVal),
        cashGrowth: Math.round(cashGrowthVal),
        propGrowth: Math.round(propGrowthVal),
        // Liquid vs illiquid split for tooltip
        liquidTotal: Math.round(d.pension + d.isa + d.cash),
        propertyEquityTotal: Math.round(d.propertyEquity + (d.rentalEquity || 0)),
      };
    });
  }, [chartData, totalAnnualContribs,
   data.pensionBalance, data.pensionBalanceDC, data.investmentsBalance, data.propertyValue]);

  // Crossover 1: annual growth >= annual contributions
  const growthExceedsContributionsAge = chartDataWithGrowth.find(
    (d, i) => i > 0 && d.annualGrowth >= d.annualContribution && d.annualContribution > 0
  )?.age ?? null;

  // Crossover 2: annual growth >= annual gross income
  const annualGrossIncome = (data.salary || 0) + (data.dividends || 0) + (data.otherIncome || 0);
  const growthExceedsIncomeAge = annualGrossIncome > 0
    ? chartDataWithGrowth.find((d, i) => i > 0 && d.annualGrowth >= annualGrossIncome)?.age ?? null
    : null;

  // ── Custom tooltip for Contributions vs Growth chart ──────────────────────
  const ContributionsTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const d = payload[0]?.payload
    if (!d) return null

    const tfmt = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`

    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-sm min-w-[220px]">
        <div className="font-bold text-ma-navy mb-3">Age {label}</div>

        {/* Contributions breakdown */}
        <div className="mb-2">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Contributions</div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.pension }}>Pension</span>
            <span className="font-medium">{tfmt(((data?.pensionMonthly || 0) + (data?.pensionCompanyFunded || 0)) * 12)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.investments }}>Investments</span>
            <span className="font-medium">{tfmt((data?.isaContribution || 0) * 12)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.cash }}>Savings</span>
            <span className="font-medium">{tfmt((data?.efTopUp || 0) * 12)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.property }}>Mortgage capital</span>
            <span className="font-medium">{tfmt(d.annualContribution - (((data?.pensionMonthly || 0) + (data?.pensionCompanyFunded || 0) + (data?.isaContribution || 0) + (data?.efTopUp || 0)) * 12))}</span>
          </div>
          <div className="flex justify-between font-semibold border-t border-gray-100 mt-1 pt-1">
            <span>Total paid in</span>
            <span>{tfmt(d.annualContribution)}</span>
          </div>
        </div>

        {/* Growth breakdown */}
        <div className="mb-2">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Growth earned</div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.pension }}>Pension</span>
            <span className="font-medium text-amber-600">{tfmt(d.pensionGrowth)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.investments }}>Investments</span>
            <span className="font-medium text-amber-600">{tfmt(d.isaGrowth)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.cash }}>Cash</span>
            <span className="font-medium text-amber-600">{tfmt(d.cashGrowth)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: POT_COLOURS.property }}>Property</span>
            <span className="font-medium text-amber-600">{tfmt(d.propGrowth)}</span>
          </div>
          <div className="flex justify-between font-semibold border-t border-gray-100 mt-1 pt-1">
            <span>Total growth</span>
            <span className="text-amber-600">{tfmt(d.annualGrowth)}</span>
          </div>
        </div>

        {/* Liquid vs illiquid split */}
        <div className="border-t border-gray-100 pt-2">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Wealth split</div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Liquid (pension + investments + cash)</span>
            <span className="font-medium">{tfmt(d.liquidTotal)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Property equity</span>
            <span className="font-medium">{tfmt(d.propertyEquityTotal)}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Net worth headline — pause-worthy (Brief 12 Change A) */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-ma-navy mb-2">Balance Sheet</h2>
        <div className="text-5xl font-bold text-ma-navy mb-2" style={{ minHeight: '3.5rem' }}>
          {fmt(netWorth)}
        </div>
        <p className="text-sm text-gray-500 italic">
          This is what you've built. Let's make sure it's in the right places.
        </p>
        {/* Wealth percentile badge */}
        {data.clientAge > 0 && (() => {
          const percentile = getWealthPercentile(netWorth, data.clientAge);
          const isAboveMedian = percentile >= 50;
          const label = isAboveMedian
            ? `Top ${100 - percentile}% for wealth at age ${data.clientAge}`
            : `Bottom ${percentile}% for wealth at age ${data.clientAge}`;
          return (
            <div className="mt-3 inline-block">
              <span className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                isAboveMedian
                  ? 'bg-green-50 text-green-700'
                  : 'bg-amber-50 text-amber-700'
              }`}>
                {label}
              </span>
            </div>
          );
        })()}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">

        {/* Cash */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" style={{ borderLeft: `4px solid ${POT_COLOURS.cash}` }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">💰</span>
            <h3 className="text-sm font-bold" style={{ color: POT_COLOURS.cash }}>Cash</h3>
          </div>

          {/* Flat list of all savings accounts — no grouping */}
          {(data.efAccount1Balance || 0) > 0 && (
            <div className="flex justify-between text-sm mb-0.5">
              <span className="text-gray-600">{data.efAccount1Name || 'Account 1'}</span>
              <span className="font-medium">{fmt(data.efAccount1Balance)}</span>
            </div>
          )}
          {(data.efAccount2Balance || 0) > 0 && (
            <div className="flex justify-between text-sm mb-0.5">
              <span className="text-gray-600">{data.efAccount2Name || 'Account 2'}</span>
              <span className="font-medium">{fmt(data.efAccount2Balance)}</span>
            </div>
          )}
          {savingsAccounts.filter(a => a.name || a.balance > 0).map((a, i) => (
            <div key={i} className="flex justify-between text-sm mb-0.5">
              <span className="text-gray-600">{a.name || `Account ${i + 1}`}</span>
              <span className="font-medium">{fmt(a.balance)}</span>
            </div>
          ))}

          {efTotal === 0 && savingsTotal === 0 && (
            <div className="text-sm text-gray-400 mb-0.5">No savings accounts entered</div>
          )}

          <div className="border-t border-emerald-200 pt-2 mt-1 flex justify-between text-sm font-bold text-emerald-800">
            <span>Total cash</span>
            <span>{fmt(totalCash)}</span>
          </div>
          {totalCash > 0 && yearsToFreedom > 0 && (
            <div className="flex justify-between text-xs text-emerald-600 mt-1">
              <span>Projected at age {data.targetFreedomAge} ({((data.assumptionCashRate ?? 0.01) * 100).toFixed(1)}%)</span>
              <span className="font-bold">{fmt(cashProjected)}</span>
            </div>
          )}
        </div>

        {/* Pension */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5" style={{ borderLeft: `4px solid ${POT_COLOURS.pension}` }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🏛️</span>
            <h3 className="text-sm font-bold" style={{ color: POT_COLOURS.pension }}>Pension</h3>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">{data.pensionProvider || 'Pension'}</span>
            <span className="font-medium">{fmt(data.pensionBalance)}</span>
          </div>
          {pensionMonthly > 0 && (
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Monthly going in</span>
              <span>{fmt(pensionMonthly)}/mo</span>
            </div>
          )}
          <div className="border-t border-blue-200 pt-2 mt-2">
            <div className="flex justify-between text-xs text-blue-600">
              <span>Projected at age {data.targetFreedomAge} ({((data.assumptionPensionGrowth ?? 0.08) * 100).toFixed(1)}%)</span>
              <span className="font-bold">{fmt(pensionProjected)}</span>
            </div>
          </div>
        </div>

        {/* ISA */}
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5" style={{ borderLeft: `4px solid ${POT_COLOURS.investments}` }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">📈</span>
            <h3 className="text-sm font-bold" style={{ color: POT_COLOURS.investments }}>Investments</h3>
          </div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">S&amp;S Investments</span>
            <span className="font-medium">{fmt(data.isaBalance)}</span>
          </div>
          {(data.isaContribution || 0) > 0 && (
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Monthly contribution</span>
              <span>{fmt(data.isaContribution)}/mo</span>
            </div>
          )}
          <div className="border-t border-teal-200 pt-2 mt-2">
            <div className="flex justify-between text-xs text-teal-600">
              <span>Projected at age {data.targetFreedomAge} ({((data.assumptionIsaGrowth ?? 0.08) * 100).toFixed(1)}%)</span>
              <span className="font-bold">{fmt(isaProjected)}</span>
            </div>
          </div>
        </div>

        {/* Property */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" style={{ borderLeft: `4px solid ${POT_COLOURS.property}` }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🏠</span>
            <h3 className="text-sm font-bold" style={{ color: POT_COLOURS.property }}>Property</h3>
          </div>
          {(data.propertyValue || 0) > 0 && (
            <>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Residential value</span>
                <span className="font-medium">{fmt(data.propertyValue)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Mortgage</span>
                <span>−{fmt(data.mortgageBalance)}</span>
              </div>
              <div className="flex justify-between text-xs text-amber-700 font-medium mb-1">
                <span>Equity</span>
                <span>{fmt(residentialEquity)}</span>
              </div>
              {data.mortgageIsInterestOnly && (
                <div className="text-xs text-amber-500 mt-1">⚠ Interest-only — balance does not reduce with payments</div>
              )}
            </>
          )}
          {(data.rentalPropertyValue || 0) > 0 && (
            <>
              <div className="border-t border-amber-200 pt-2 mt-2" />
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Rental value</span>
                <span className="font-medium">{fmt(data.rentalPropertyValue)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Rental mortgage</span>
                <span>−{fmt(data.rentalMortgageBalance)}</span>
              </div>
              <div className="flex justify-between text-xs text-amber-700 font-medium">
                <span>Rental equity</span>
                <span>{fmt(rentalEquity)}</span>
              </div>
              {data.rentalMortgageIsInterestOnly && (
                <div className="text-xs text-amber-500 mt-1">⚠ Interest-only — capital not reducing</div>
              )}
              {(data.rentalIncomeNet || 0) > 0 && (
                <div className="flex justify-between text-xs text-green-600 mt-1">
                  <span>Net rental income</span>
                  <span>{fmt(data.rentalIncomeNet)}/year</span>
                </div>
              )}
            </>
          )}
          <div className="border-t border-amber-200 pt-2 mt-2 flex justify-between text-sm font-bold text-amber-800">
            <span>Total equity</span>
            <span>{fmt(totalPropertyEquity)}</span>
          </div>
        </div>

        {/* Business */}
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-5" style={{ borderLeft: '4px solid #9CA3AF' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🏢</span>
            <h3 className="text-sm font-bold text-purple-800">Business</h3>
          </div>
          <p className="text-sm text-gray-600">Business value and retained earnings — covered in Session 2.</p>
        </div>

        {/* DB Pension */}
        {data.hasDbPension && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">🎯</span>
              <h3 className="text-sm font-bold text-indigo-800">Defined Benefit Pension</h3>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">{data.dbPensionProvider || 'DB Pension'}</span>
              <span className="font-medium">{fmt(data.dbPensionAnnualIncome)}/year</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Starts at age</span>
              <span>{data.dbPensionStartAge || 67}</span>
            </div>
            {data.dbPensionIndexLinked && (
              <div className="text-xs text-green-600 mt-1">✓ Index-linked — income rises with inflation</div>
            )}
            <div className="border-t border-indigo-200 pt-2 mt-2">
              <div className="text-xs text-indigo-600">
                {(data.dbPensionStartAge || 67) <= data.targetFreedomAge
                  ? 'Included as offset in Freedom Number calculation'
                  : `Starts after freedom age (${data.dbPensionStartAge}) — not included in Freedom Number`}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* HOW YOU COMPARE — Wealth benchmarking breakdown */}
      {clientAge > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy uppercase tracking-wide mb-1">
            How You Compare — Wealth at age {clientAge}
          </h3>
          <p className="text-xs text-gray-400 mb-4">Based on ONS Wealth and Assets Survey. For context only.</p>
          <div className="space-y-3">
            {[
              { label: 'Pension wealth', value: pensionTotal, pct: pensionPct },
              { label: 'Property equity', value: totalPropertyEquity, pct: propertyPct },
              { label: 'Cash & investments', value: financialTotal, pct: financialPct },
              { label: 'Net worth', value: netWorth, pct: netWorthPct },
            ].map((row) => {
              const badge = benchBadge(row.pct);
              return (
                <div key={row.label} className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-700">{row.label}</span>
                    <span className="text-sm font-medium text-ma-navy ml-3">{fmt(row.value)}</span>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${badge.cls}`}>
                    {badge.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Wealth Trajectory — Year by Year chart */}
      {maxYears > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-ma-navy uppercase tracking-wide">
              Wealth Trajectory — Year by Year
            </h3>
            <span className="text-xs text-gray-400">To age {freedomAge}</span>
          </div>
          <ResponsiveContainer width="100%" height={320} minHeight={200}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
              <XAxis
                dataKey="age"
                tick={{ fill: '#6b7280', fontSize: 11 }}
                label={{ value: 'Age', position: 'insideBottomRight', offset: -5, fill: '#9ca3af', fontSize: 11 }}
              />
              <YAxis
                tickFormatter={(v: number) => `£${Math.round(v / 1000)}k`}
                tick={{ fill: '#6b7280', fontSize: 11 }}
              />
              <Tooltip formatter={(v: any) => `£${Number(v).toLocaleString('en-GB')}`} />
              <Legend />

              {/* Freedom age vertical reference line */}
              <ReferenceLine
                x={freedomAge}
                stroke="#C9A84C"
                strokeDasharray="4 4"
                label={{ value: 'Freedom age', position: 'top', fill: '#C9A84C', fontSize: 11 }}
              />

              {/* Freedom Number horizontal reference line */}
              {(data.freedomNumberInflated || 0) > 0 && (
                <ReferenceLine
                  y={data.freedomNumberInflated || 0}
                  stroke="#C9A84C"
                  strokeWidth={2}
                  label={{ value: 'Freedom Number', position: 'right', fill: '#C9A84C', fontSize: 11 }}
                />
              )}

              {/* Stacked areas — ALL areas declared BEFORE lines */}
              <Area type="monotone" dataKey="cash" stackId="1"
                fill={POT_COLOURS.cash} stroke={POT_COLOURS.cash} name="Cash" />
              <Area type="monotone" dataKey="isa" stackId="1"
                fill={POT_COLOURS.investments} stroke={POT_COLOURS.investments} name="Investments" />
              <Area type="monotone" dataKey="propertyEquity" stackId="1"
                fill={POT_COLOURS.property} stroke={POT_COLOURS.property} name="Property equity" />
              <Area type="monotone" dataKey="pension" stackId="1"
                fill={POT_COLOURS.pension} stroke={POT_COLOURS.pension} name="Pension" />

              {/* Mortgage line — declared AFTER areas */}
              <Line type="monotone" dataKey="mortgage"
                stroke={POT_COLOURS.mortgage} strokeWidth={2} dot={false}
                name="Mortgage balance" />
            </ComposedChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-400 mt-3">
            Projected growth using {((data.assumptionPensionGrowth ?? 0.08) * 100).toFixed(0)}% pension,{' '}
            {((data.assumptionIsaGrowth ?? 0.08) * 100).toFixed(0)}% investments,{' '}
            {((data.assumptionPropertyGrowth ?? 0.01) * 100).toFixed(0)}% property growth. For illustration only.
          </p>
        </div>
      )}

      {/* How Your Wealth Is Growing — Cumulative contributions vs growth (Brief 46B S2) */}
      {maxYears > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 mt-4 mb-6">
          <div className="mb-1">
            <h3 className="text-sm font-semibold text-ma-navy uppercase tracking-wide">
              How Your Wealth Is Growing
            </h3>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            How much you put in vs how much your money earns — and when growth takes over.
          </p>

          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData} margin={{ top: 20, right: 60, left: 20, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="age"
                label={{ value: 'Age', position: 'insideBottom', offset: -10, fontSize: 11 }}
              />
              <YAxis tickFormatter={(v: number) => {
                if (v >= 1000000) return `£${(v / 1000000).toFixed(1)}m`;
                return `£${Math.round(v / 1000)}k`;
              }} />
              <Tooltip
                content={({ active, payload, label }: any) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  if (!d) return null;
                  const tfmt = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`;
                  return (
                    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-4 text-sm min-w-[200px]">
                      <div className="font-bold text-ma-navy mb-2">Age {label}</div>
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-600">Contributions</span>
                        <span className="font-medium">{tfmt(d.contributions)}</span>
                      </div>
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-600">Growth</span>
                        <span className="font-medium" style={{ color: '#14B8A6' }}>{tfmt(d.liquidGrowth)}</span>
                      </div>
                      <div className="flex justify-between font-semibold border-t border-gray-100 mt-1 pt-1">
                        <span>Total liquid</span>
                        <span>{tfmt(d.liquidTotal)}</span>
                      </div>
                    </div>
                  );
                }}
              />
              <Legend verticalAlign="top" />

              {/* Freedom Number reference line — BEFORE bars */}
              {(data.freedomNumberInflated || 0) > 0 && (
                <ReferenceLine
                  y={data.freedomNumberInflated || 0}
                  stroke="#C9A84C"
                  strokeDasharray="8 4"
                  strokeWidth={2}
                  label={{ value: 'Freedom Number', position: 'insideTopRight', fill: '#C9A84C', fontSize: 11, fontWeight: 'bold' }}
                />
              )}

              {/* Crossover reference lines */}
              {growthExceedsContributionsAge && (
                <ReferenceLine
                  x={growthExceedsContributionsAge}
                  stroke="#C9A84C"
                  strokeDasharray="4 4"
                  label={{ value: 'Growth > contributions', position: 'top', fill: '#C9A84C', fontSize: 10 }}
                />
              )}
              {growthExceedsIncomeAge && (
                <ReferenceLine
                  x={growthExceedsIncomeAge}
                  stroke="#10B981"
                  strokeDasharray="4 4"
                  label={{ value: 'Growth > income', position: 'top', fill: '#10B981', fontSize: 10 }}
                />
              )}

              {/* Stacked bars — contributions (navy) bottom, growth (teal) top */}
              <Bar dataKey="contributions" stackId="w" fill="#1E3A5F" name="Contributions" />
              <Bar dataKey="liquidGrowth" stackId="w" fill="#14B8A6" name="Growth" />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Compounding callout */}
          {chartData.length > 0 && (() => {
            const final = chartData[chartData.length - 1];
            return final.liquidGrowth > 0 ? (
              <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-xl">
                <p className="text-sm text-gray-700">
                  At age {freedomAge}, <span className="font-bold" style={{ color: '#14B8A6' }}>{fmt(final.liquidGrowth)}</span> of
                  your total liquid wealth comes from investment growth — not money you put in.
                  That's the power of long-term compounding.
                </p>
              </div>
            ) : null;
          })()}

          {/* Crossover callout cards */}
          <div className="mt-4 space-y-2">
            {growthExceedsContributionsAge && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <span className="text-amber-600 font-bold text-sm whitespace-nowrap">Age {growthExceedsContributionsAge}</span>
                <span className="text-sm text-gray-600">
                  Your money has earned more than you've ever put in. Compound interest has taken over.
                </span>
              </div>
            )}
            {growthExceedsIncomeAge && (
              <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                <span className="text-green-700 font-bold text-sm whitespace-nowrap">Age {growthExceedsIncomeAge}</span>
                <span className="text-sm text-gray-600">
                  Your wealth grows faster than you earn. Work becomes a choice, not a necessity.
                </span>
              </div>
            )}
            {!growthExceedsContributionsAge && !growthExceedsIncomeAge && (
              <p className="text-xs text-gray-400 italic">
                Neither crossover is reached by freedom age on the current trajectory.
                The decisions made in today's session can change that.
              </p>
            )}
            {growthExceedsContributionsAge && !growthExceedsIncomeAge && (
              <p className="text-xs text-gray-400 italic">
                The growth-exceeds-income crossover is not reached by freedom age on the current trajectory.
                The pension lever directly accelerates this.
              </p>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Contributions = starting balances + pension, ISA and savings paid in over time (liquid assets only).
            Growth = compound returns earned on liquid assets. Property equity excluded.
          </p>
        </div>
      )}

      {/* Liquid vs Illiquid at Freedom Age (Brief 46B S3) */}
      {yearsToFreedom > 0 && (() => {
        const liquidAtFreedom = cashProjected + pensionProjected + isaProjected;
        const illiquidAtFreedom = Math.max(0, residentialEquityAtFreedom) + Math.max(0, rentalEquityAtFreedom);
        const totalAtFreedom = liquidAtFreedom + illiquidAtFreedom;
        return totalAtFreedom > 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
            <h3 className="text-sm font-semibold text-ma-navy uppercase tracking-wide mb-1">
              Liquid vs Illiquid — At Age {data.targetFreedomAge}
            </h3>
            <p className="text-xs text-gray-400 mb-4">Projected wealth split at your freedom age.</p>
            <div className="flex gap-1 h-6 rounded-full overflow-hidden bg-gray-100">
              <div className="bg-teal-400 rounded-l-full flex items-center justify-center"
                style={{ width: `${(liquidAtFreedom / totalAtFreedom) * 100}%` }}>
                {liquidAtFreedom / totalAtFreedom > 0.15 && (
                  <span className="text-[10px] text-white font-bold">{Math.round((liquidAtFreedom / totalAtFreedom) * 100)}%</span>
                )}
              </div>
              <div className="bg-amber-400 rounded-r-full flex items-center justify-center"
                style={{ width: `${(illiquidAtFreedom / totalAtFreedom) * 100}%` }}>
                {illiquidAtFreedom / totalAtFreedom > 0.15 && (
                  <span className="text-[10px] text-white font-bold">{Math.round((illiquidAtFreedom / totalAtFreedom) * 100)}%</span>
                )}
              </div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>Liquid <span className="font-semibold text-teal-600">{fmt(Math.round(liquidAtFreedom))}</span></span>
              <span>Property equity <span className="font-semibold text-amber-600">{fmt(Math.round(illiquidAtFreedom))}</span></span>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Liquid assets are your drawdown pot. Property equity contributes to net worth but is not included in your Freedom Number calculation.
            </p>
          </div>
        ) : null;
      })()}

      {/* Summary — Today vs At Freedom Age */}
      <div className="bg-ma-navy rounded-2xl p-6 text-white mb-6">
        <div className="grid grid-cols-2 gap-6">
          {/* Today column */}
          <div>
            <div className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-4 text-center">Today</div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-white/60 text-sm">Total assets</span>
                <span className="text-lg font-bold">{fmt(totalAssets)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60 text-sm">Total debt</span>
                <span className="text-lg font-bold text-red-300">{fmt(totalDebt)}</span>
              </div>
              <div className="border-t border-white/10 pt-3">
                <div className="flex justify-between">
                  <span className="text-ma-gold text-sm font-semibold">Net worth</span>
                  <span className="text-2xl font-bold">{fmt(netWorth)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* At Freedom Age column */}
          <div>
            <div className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-4 text-center">
              At age {data.targetFreedomAge}
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-white/60 text-sm">Total assets</span>
                <span className="text-lg font-bold">{fmt(Math.round(projectedTotalAssets))}</span>
              </div>
              {projectedTotalDebt > 0 && (
                <div className="flex justify-between">
                  <span className="text-white/60 text-sm">Total debt</span>
                  <span className="text-lg font-bold text-red-300">{fmt(Math.round(projectedTotalDebt))}</span>
                </div>
              )}
              <div className="border-t border-white/10 pt-3">
                <div className="flex justify-between">
                  <span className="text-ma-gold text-sm font-semibold">Net worth</span>
                  <span className="text-2xl font-bold">{fmt(Math.round(projectedNetWorthAtFreedom))}</span>
                </div>
                <p className="text-white/30 text-[10px] mt-2">Approximate — Voyant gives the definitive figure</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Liquid vs Illiquid bar */}
      <div className="bg-ma-navy rounded-2xl p-4 text-white">
        <div className="text-white/60 text-xs uppercase tracking-wide mb-2">Liquid vs illiquid (today)</div>
        <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-white/10">
          {totalAssets > 0 && (
            <>
              <div className="bg-teal-400 rounded-l-full" style={{ width: `${(liquid / (liquid + illiquid)) * 100}%` }} />
              <div className="bg-amber-400 rounded-r-full" style={{ width: `${(illiquid / (liquid + illiquid)) * 100}%` }} />
            </>
          )}
        </div>
        <div className="flex justify-between text-xs text-white/50 mt-1">
          <span>Liquid {fmt(liquid)}</span>
          <span>Property {fmt(illiquid)}</span>
        </div>
      </div>

      {!showReady && (
        <div className="mt-4 text-center text-xs text-gray-400 animate-pulse">
          Let this number land...
        </div>
      )}
    </div>
  );
}
